/**
 * ERP 功能能力目录的版本化同步契约（ADR-0004）。
 *
 * 核心约束：
 *   1. 控制面不发明能力名称。能力集合的唯一来源是 ERP 的
 *      `app/shared/tenantContract.ts`（TENANT_FEATURES），本文件是它的镜像契约。
 *   2. 契约版本 `CAPABILITY_CATALOG_CONTRACT_VERSION` 只在发生破坏性变更时递增。
 *   3. 同步是单向的：ERP → 控制面。控制面只允许给能力补充「中文名称 / 说明 / 分组」，
 *      不允许新增或删除能力键。
 *   4. 同步结果必须留痕（capability_catalog_sync_runs），能证明一致或定位漂移。
 */
import { z } from "zod";
/** 契约版本。破坏性变更（键含义改变、分组语义改变、字段增减）必须递增。 */
export const CAPABILITY_CATALOG_CONTRACT_VERSION = "1";
/** ERP 侧来源文件路径，同步脚本据此读取并校验。 */
export const ERP_CAPABILITY_SOURCE_PATH = "app/shared/tenantContract.ts";
/** ERP 侧导出常量名。 */
export const ERP_CAPABILITY_EXPORT_NAME = "TENANT_FEATURES";
/** 能力所属业务域，仅用于界面分组展示，不改变能力语义。 */
export const capabilityDomains = [
    "dashboard",
    "platform_store",
    "content_live",
    "product",
    "inventory",
    "order_fulfillment",
    "customer_service",
    "data",
    "ai",
    "automation",
    "hr",
    "finance",
    "system",
    "integrations",
];
export const capabilityDomainSchema = z.enum(capabilityDomains);
export const capabilityDomainLabels = {
    dashboard: "经营总览",
    platform_store: "平台与店铺",
    content_live: "内容与直播",
    product: "商品与供应链",
    inventory: "库存与生产",
    order_fulfillment: "订单与履约",
    customer_service: "客户服务",
    data: "数据与分析",
    ai: "AI 能力",
    automation: "自动化",
    hr: "人事与薪酬",
    finance: "财务",
    system: "系统与研发",
    integrations: "外部集成",
};
/**
 * ERP 正式能力目录：29 项。
 *
 * 与 `app/shared/tenantContract.ts` 的 TENANT_FEATURES 一一对应。
 * 修改本清单前必须先修改 ERP 侧并重新同步，否则同步任务会判定为 drift 并阻断发布。
 */
export const CAPABILITY_CATALOG_SOURCE = [
    { key: "dashboard.center", domain: "dashboard", displayName: "经营总览", description: "查看企业经营看板与关键指标概览。", menuKeyPrefixes: ["dashboard"] },
    {
        key: "platform.center", domain: "platform_store", displayName: "平台授权中心",
        description: "管理电商平台的官方授权、接口配置与连接状态。",
        menuKeyPrefixes: ["platform_center", "store_operation.shops", "settings.platformApi", "settings.platforms"],
    },
    { key: "store.operation", domain: "platform_store", displayName: "店铺运营", description: "日常店铺运营、商品上架与平台同步。", menuKeyPrefixes: ["store_operation"] },
    { key: "store.resources", domain: "platform_store", displayName: "运营资源", description: "运营素材、资源中心与运营工具。", menuKeyPrefixes: ["operation_center", "resource_center"] },
    { key: "creator.operation", domain: "content_live", displayName: "创作者运营", description: "达人内容与创作者合作管理。", menuKeyPrefixes: ["creator_operation"] },
    { key: "live.operation", domain: "content_live", displayName: "直播运营", description: "直播场次、直播数据与直播复盘。", menuKeyPrefixes: ["live_operation"] },
    {
        key: "product.management", domain: "product", displayName: "商品管理",
        description: "商品资料、商品库与商品分类维护。",
        menuKeyPrefixes: ["products", "store_operation.product_library", "store_operation.product_categories"],
    },
    { key: "product.supplier-bindings", domain: "product", displayName: "供应商商品绑定", description: "把商品与供应商供货关系绑定。", menuKeyPrefixes: [] },
    {
        key: "inventory.management", domain: "inventory", displayName: "库存管理",
        description: "库存台账、出入库与库位管理。",
        menuKeyPrefixes: ["inventory", "settings.warehouseLocations"],
    },
    { key: "inventory.manufacturer-operations", domain: "inventory", displayName: "厂家代发作业", description: "厂家代发相关的收发与对账作业。", menuKeyPrefixes: ["inventory.manufacturer"] },
    {
        key: "order.center", domain: "order_fulfillment", displayName: "订单中心",
        description: "订单处理、打单发货与快递策略。",
        menuKeyPrefixes: ["order_center", "settings.print", "system_center.print", "settings.expressStrategies", "settings.printerRouting"],
    },
    { key: "fulfillment.picking-modes", domain: "order_fulfillment", displayName: "拣货模式", description: "单品与多品拣货模式的切换能力（功能级，不绑定菜单）。", menuKeyPrefixes: [] },
    { key: "fulfillment.supplier-distribution-push", domain: "order_fulfillment", displayName: "供应商分单推送", description: "把订单按规则推送给供应商发货。", menuKeyPrefixes: ["order_center.supplier_push"] },
    { key: "fulfillment.bundle-gifts", domain: "order_fulfillment", displayName: "组合商品与赠品", description: "审单时的组合商品拆分与赠品规则。", menuKeyPrefixes: ["fulfillment_rules"] },
    { key: "order.aftersales", domain: "order_fulfillment", displayName: "售后管理", description: "退换货、退款与售后协商处理。", menuKeyPrefixes: ["orders"] },
    { key: "customer.service-workbench", domain: "customer_service", displayName: "客服工作台", description: "客服会话、售后跟进与处理记录。", menuKeyPrefixes: ["order_center.customer_service"] },
    { key: "content.center", domain: "content_live", displayName: "内容中心", description: "图文、视频等内容素材的管理与分发。", menuKeyPrefixes: ["content"] },
    { key: "data.center", domain: "data", displayName: "数据中心", description: "经营数据报表与数据导出。", menuKeyPrefixes: ["data_center"] },
    { key: "data.analytics", domain: "data", displayName: "数据分析", description: "多维分析与经营诊断。", menuKeyPrefixes: ["data"] },
    { key: "ai.operation", domain: "ai", displayName: "AI 运营", description: "AI 辅助的运营建议与自动化处理。", menuKeyPrefixes: ["ai_operation_center"] },
    { key: "ai.brain", domain: "ai", displayName: "AI 经营大脑", description: "AI 中枢、经营大脑与小面 AI 能力。", menuKeyPrefixes: ["ai_brain", "ai_operating_brain_center", "xiao_mian_ai_center"] },
    { key: "automation.center", domain: "automation", displayName: "自动化中心", description: "自动化任务编排与执行记录。", menuKeyPrefixes: ["automation_center"] },
    { key: "automation.capabilities", domain: "automation", displayName: "自动化能力", description: "自动化规则、触发器与动作配置。", menuKeyPrefixes: ["automation"] },
    { key: "hr.schedule-payroll", domain: "hr", displayName: "排班与薪酬", description: "员工排班、考勤与薪酬核算。", menuKeyPrefixes: ["personnel", "schedule_payroll_center"] },
    { key: "finance.invoice-applications", domain: "finance", displayName: "开票申请", description: "发票开具申请与流转（功能级，不绑定菜单）。", menuKeyPrefixes: [] },
    { key: "finance.center", domain: "finance", displayName: "财务中心", description: "收支、对账、利润与财务报表。", menuKeyPrefixes: ["finance"] },
    { key: "system.center", domain: "system", displayName: "系统管理", description: "账号、权限、参数与系统配置。", menuKeyPrefixes: ["system_center", "settings", "engineering_repair_center"] },
    { key: "developer.center", domain: "system", displayName: "开发者中心", description: "开放接口、密钥与开发者工具。", menuKeyPrefixes: ["developer_center"] },
    { key: "integrations.center", domain: "integrations", displayName: "外部集成", description: "第三方系统对接与集成配置。", menuKeyPrefixes: ["integrations"] },
];
/** 能力键必须唯一，重复即视为契约破损。 */
export const CAPABILITY_KEYS = CAPABILITY_CATALOG_SOURCE.map((item) => item.key);
/** ERP 正式能力数量。同步结果与它不一致时必须判定为 drift 并阻断发布。 */
export const ERP_CAPABILITY_COUNT = CAPABILITY_CATALOG_SOURCE.length;
export const capabilityKeySchema = z.enum(CAPABILITY_KEYS);
/** ERP 侧导出的单条能力记录。 */
export const erpCapabilityRecordSchema = z.object({
    key: z.string().regex(/^[a-z0-9][a-z0-9._-]{1,119}$/),
    menuKeyPrefixes: z.array(z.string().max(120)),
});
/** ERP 侧导出文件的形状。 */
export const erpCapabilityExportSchema = z.object({
    contractVersion: z.string().min(1).max(20).default(CAPABILITY_CATALOG_CONTRACT_VERSION),
    sourcePath: z.string().min(1).max(200),
    /** 源文件内容校验和（SHA-256），用于判断是否需要重新同步。 */
    checksum: z.string().regex(/^[a-f0-9]{64}$/),
    capabilities: z.array(erpCapabilityRecordSchema).min(1),
});
/** 同步差异。 */
export const capabilitySyncDiffSchema = z.object({
    addedKeys: z.array(z.string()).default([]),
    removedKeys: z.array(z.string()).default([]),
    changedKeys: z.array(z.object({ key: z.string(), fields: z.array(z.string()) })).default([]),
});
/** 同步结果。success 表示与契约完全一致；drift 表示 ERP 侧已变更，需要人工确认后升级契约。 */
export const capabilitySyncResultSchema = capabilitySyncDiffSchema.extend({
    contractVersion: z.string(),
    sourceRef: z.string(),
    sourceChecksum: z.string(),
    keyCount: z.number().int().nonnegative(),
    status: z.enum(["success", "drift", "failed"]),
    message: z.string().default(""),
});
/** 控制面目录项（读取模型）。 */
export const capabilityCatalogItemSchema = z.object({
    id: z.string().uuid(),
    capabilityKey: z.string(),
    domain: capabilityDomainSchema,
    domainLabel: z.string(),
    displayName: z.string(),
    description: z.string(),
    lifecycle: z.enum(["active", "deprecated"]),
    /** 已被多少个已发布套餐版本引用；大于 0 时不允许从目录移除。 */
    referencedByPublishedPlans: z.number().int().nonnegative().default(0),
    removedAt: z.string().datetime().nullable(),
    updatedAt: z.string().datetime(),
});
